// src/components/QuickAddTransaction.js
//
// Phone 3 — Manual Add. Stripped-down "cash, no-receipt" flow.
// Amount (custom numpad) → merchant (with auto-suggest pill) → quick
// category pills → save. Today's date assumed, last-used payment_method
// auto-attached, tax status defaults to Personal.
//
// Tapping the scan icon hands off to ReceiptScanner, which owns its own
// post-AI Confirm flow with the full field list (Phone 2).
//
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { emitDataEvent, EVENTS } from '../lib/dataEvents'
import { usePartner } from '../lib/usePartner'
import { theme } from '../lib/theme'
import ReceiptScanner from './ReceiptScanner'
import MatchPrompt from './MatchPrompt'
import { detectMatch } from '../lib/merchantMatching'
import { dedupeCategories, resolveCategoryId, fetchHouseholdCategories } from '../lib/categories'

const norm = (s) => (s || '').toLowerCase().trim()

export default function QuickAddTransaction({ onClose, onSaved, prefill = null }) {
  const { partnerId } = usePartner()

  // ─── Form state ───────────────────────────────────────
  // Amount is a string built up by the numpad (so leading-zero / partial-cent
  // states render correctly). Coerced to float on save.
  const [amount, setAmount] = useState(prefill?.amount?.toString() || '')
  const [merchantName, setMerchantName] = useState(prefill?.merchant_name || '')
  const [categoryId, setCategoryId] = useState(prefill?.category_id || '')
  const [paymentMethodId, setPaymentMethodId] = useState(null)
  const [paymentMethods, setPaymentMethods] = useState([])
  // Today as YYYY-MM-DD in local time (avoids timezone shift)
  const todayStr = (() => {
    const d = new Date()
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  })()
  const [txDate, setTxDate] = useState(prefill?.transaction_date || todayStr)
  const [txType, setTxType] = useState(
    prefill?.is_business ? 'business' : prefill?.is_charitable ? 'charitable' : 'personal'
  )


  // ─── Data state ───────────────────────────────────────
  const [categories, setCategories] = useState([])       // deduped (one per canonical name)
  const [allCategoryRows, setAllCategoryRows] = useState([]) // raw rows, for resolving old-month ids
  const [targetBudgetId, setTargetBudgetId] = useState(null)
  const [merchantHistory, setMerchantHistory] = useState([])
  const [recentCategoryIds, setRecentCategoryIds] = useState([])

  // ─── UI state ─────────────────────────────────────────
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [matchPrompt, setMatchPrompt] = useState(null) // Chunk 5: bill/debt match confirmation step
  const [showScanner, setShowScanner] = useState(false)
  const [showAllCats, setShowAllCats] = useState(false)
  const [catSearch, setCatSearch] = useState('')
  const [showCreateCat, setShowCreateCat] = useState(false)
  const [newCatName, setNewCatName] = useState('')
  const [creatingCat, setCreatingCat] = useState(false)
  const [newCatParent, setNewCatParent] = useState(null)
  const merchantRef = useRef(null)

  const today = new Date().toISOString().split('T')[0]

  // ─── Initial fetch ────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData() }, [partnerId])

  const fetchData = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    const userIds = [user.id, ...(partnerId ? [partnerId] : [])]

    const now = new Date()
    const month = now.getMonth() + 1
    const year = now.getFullYear()

    // Find current-month budget first; if none, fall back to most recent budget
    let budgetIds = []
    const currentBudgetRes = await supabase.from('budgets').select('id')
      .in('user_id', userIds).eq('month', month).eq('year', year)
      .eq('is_active', true).order('created_at')
    if ((currentBudgetRes.data || []).length > 0) {
      budgetIds = currentBudgetRes.data.map(b => b.id)
    } else {
      const fallbackRes = await supabase.from('budgets').select('id')
        .in('user_id', userIds).eq('is_active', true)
        .order('year', { ascending: false }).order('month', { ascending: false })
        .limit(1)
      budgetIds = (fallbackRes.data || []).map(b => b.id)
    }

    // New categories are created in this budget (current month, else most recent).
    setTargetBudgetId(budgetIds[0] || null)

    // All household category rows (every month, user + partner) deduped by
    // canonical name — see src/lib/categories.js. Loading only the current
    // budget still duplicated when both partners had a budget, and left
    // merchant-history / recent ids from older months unresolvable.
    const [catData, txRes, pmRes] = await Promise.all([
      fetchHouseholdCategories(supabase, userIds),
      supabase.from('transactions')
        .select('merchant_name, category_id, amount, payment_method_id')
        .eq('user_id', user.id)
        .order('transaction_date', { ascending: false })
        .limit(200),
      supabase.from('payment_methods').select('*').in('user_id', userIds),
    ])

    setAllCategoryRows(catData.rows)
    setCategories(dedupeCategories(catData.rows, { budgets: catData.budgets, userId: user.id }))

    if (txRes.data) {
      // Merchant history map: most recent merchant + its category & last amount
      const seen = {}
      txRes.data.forEach(tx => {
        if (tx.merchant_name && !seen[tx.merchant_name.toLowerCase()]) {
          seen[tx.merchant_name.toLowerCase()] = {
            name: tx.merchant_name,
            category_id: tx.category_id,
            amount: tx.amount,
          }
        }
      })
      setMerchantHistory(Object.values(seen))

      // Recent category usage — for the quick-cat pill strip
      const distinct = []
      const catSeen = new Set()
      txRes.data.forEach(tx => {
        if (tx.category_id && !catSeen.has(tx.category_id)) {
          catSeen.add(tx.category_id)
          distinct.push(tx.category_id)
        }
      })
      setRecentCategoryIds(distinct.slice(0, 8))

      // Payment-method-as-smart-default — most-used method for this user.
      // Persisted on save even though we don't expose it in this stripped UI;
      // user can edit it later via the Log row's edit view.
      const myTxs = txRes.data.filter(t => t.payment_method_id)
      if (myTxs.length > 0) {
        const counts = {}
        myTxs.forEach(t => { counts[t.payment_method_id] = (counts[t.payment_method_id] || 0) + 1 })
        const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]
        if (top && !prefill?.payment_method_id) setPaymentMethodId(top[0])
      }
    }

    setPaymentMethods(pmRes.data || [])
  }

  // ─── Merchant auto-suggest ───────────────────────────
  // Finds the best-matching previous merchant and its category.
  const merchantMatch = useMemo(() => {
    if (!merchantName || merchantName.length < 2) return null
    const lower = merchantName.toLowerCase()
    return merchantHistory.find(m =>
      m.name.toLowerCase().startsWith(lower) ||
      m.name.toLowerCase().includes(lower)
    ) || null
  }, [merchantName, merchantHistory])

  const suggestedCategory = useMemo(() => {
    if (!merchantMatch?.category_id) return null
    const id = resolveCategoryId(merchantMatch.category_id, allCategoryRows, categories)
    return categories.find(c => c.id === id) || null
  }, [merchantMatch, categories, allCategoryRows])

  // Auto-set category from merchant suggestion if user hasn't picked one yet
  useEffect(() => {
    if (!categoryId && suggestedCategory) {
      setCategoryId(suggestedCategory.id)
    }
  }, [suggestedCategory, categoryId])

  // ─── Numpad handlers ─────────────────────────────────
  const handleNumpadKey = (key) => {
    setAmount(prev => {
      if (key === 'del') {
        return prev.slice(0, -1)
      }
      if (key === '.') {
        if (prev.includes('.')) return prev
        if (prev === '') return '0.'
        return prev + '.'
      }
      // numeric key
      if (prev === '0') return key
      // Already has 2 decimals — block further entry
      const dotIdx = prev.indexOf('.')
      if (dotIdx !== -1 && prev.length - dotIdx > 2) return prev
      // Cap at reasonable length
      if (prev.replace('.', '').length >= 9) return prev
      return prev + key
    })
  }

  // ─── Format hero amount: split into whole and cents ──
  const amountDisplay = useMemo(() => {
    if (!amount || amount === '0') {
      return { whole: '0', cents: '00', isPlaceholder: true }
    }
    if (amount === '0.') {
      return { whole: '0', cents: '', isPlaceholder: false, hasDot: true }
    }
    const dotIdx = amount.indexOf('.')
    let whole, cents, hasDot = false
    if (dotIdx === -1) {
      whole = amount
      cents = '00'
    } else {
      whole = amount.slice(0, dotIdx) || '0'
      cents = amount.slice(dotIdx + 1).padEnd(2, '0').slice(0, 2)
      hasDot = true
    }
    const wholeNum = parseInt(whole, 10) || 0
    const wholeFormatted = wholeNum.toLocaleString('en-US')
    return { whole: wholeFormatted, cents, isPlaceholder: false, hasDot }
  }, [amount])

  // ─── Scan handoff ────────────────────────────────────
  // ReceiptScanner owns its own Save flow when mode='receipt'. We only
  // get called back if the user backs out of the scanner; in that case
  // we just close it and stay in manual flow.
  const handleScannerSaved = () => {
    setShowScanner(false)
    onSaved()
  }

  // ─── Save (manual) ───────────────────────────────────
  const handleSave = async () => {
    const num = parseFloat(amount)
    if (!num || !categoryId || saving) return
    setSaving(true)
    const { data: { user } } = await supabase.auth.getUser()
    const userIds = [user.id, ...(partnerId ? [partnerId] : [])]

     const txMonth = new Date(txDate + 'T12:00:00').getMonth() + 1
    const txYear = new Date(txDate + 'T12:00:00').getFullYear()
    const { data: budget } = await supabase.from('budgets').select('id')
      .in('user_id', userIds).eq('month', txMonth).eq('year', txYear)
      .eq('is_active', true).order('created_at').limit(1).maybeSingle()

    const { data: inserted } = await supabase.from('transactions').insert({
      user_id: user.id,
      budget_id: budget?.id,
      category_id: categoryId,
      amount: num,
      type: 'expense',
      merchant_name: merchantName || null,
      transaction_date: txDate,
      is_business: txType === 'business',
      is_charitable: txType === 'charitable',
      payment_method_id: paymentMethodId || null,
    }).select('id').single()

    // ─── Auto-match-then-confirm (Chunk 5) ────────────────
    // After the transaction is saved, check whether its merchant matches a bill
    // or debt. A saved rule applies silently; an unmatched hit surfaces the
    // MatchPrompt step before we close.
    if (inserted && (merchantName || '').trim()) {
      const [{ data: billsData }, { data: debtsData }] = await Promise.all([
        supabase.from('bills').select('id, name, amount, category_id').in('user_id', userIds).eq('is_active', true),
        supabase.from('debts').select('id, name, current_balance, monthly_payment').in('user_id', userIds),
      ])
      const detection = await detectMatch({
        transaction: { id: inserted.id, merchant_name: merchantName, amount: num, transaction_date: txDate },
        bills: billsData || [], debts: debtsData || [], userId: user.id,
      })
      if (detection.status === 'prompt') {
        setSaving(false)
        setMatchPrompt({
          transactions: [{ id: inserted.id, amount: num, transaction_date: txDate }],
          kind: detection.kind, target: detection.target,
          bills: billsData || [], debts: debtsData || [], userId: user.id,
        })
        return
      }
    }

    setSaving(false)
    setSaved(true)
    setTimeout(() => { onSaved() }, 700)
  }

  // ─── Keyboard shortcuts ──────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (showAllCats) { setShowAllCats(false); return }
        onClose()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleSave()

      // Physical keyboard input for amount — only when not focused in another input/textarea
      const target = e.target
      const isInInput = target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      )
      if (isInInput) return
      if (showAllCats) return // category picker is open, don't intercept

      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault()
        handleNumpadKey(e.key)
      } else if (e.key === '.') {
        e.preventDefault()
        handleNumpadKey('.')
      } else if (e.key === 'Backspace') {
        e.preventDefault()
        handleNumpadKey('del')
      } else if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) {
        // Plain Enter triggers save
        e.preventDefault()
        handleSave()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [amount, categoryId, merchantName, paymentMethodId, showAllCats])

  // ─── Quick-cat data ──────────────────────────────────
  const recentCategories = useMemo(() => {
    // Recent ids span months — resolve each to its deduped row, then dedupe.
    const ids = recentCategoryIds.map(id => resolveCategoryId(id, allCategoryRows, categories))
    return Array.from(new Set(ids.filter(Boolean)))
      .map(id => categories.find(c => c.id === id))
      .filter(Boolean)
      .slice(0, 5)
  }, [recentCategoryIds, categories, allCategoryRows])

  // Defensive dedupe by id (some upstream source occasionally returns dupes)
  const parentCategories = useMemo(() => {
    const seen = new Set()
    return categories.filter(c => {
      if (c.parent_id) return false
      if (seen.has(c.id)) return false
      seen.add(c.id)
      return true
    })
  }, [categories])
  // prefill.category_id may be an older month's id — resolve by canonical name.
  const effectiveCatId = resolveCategoryId(categoryId, allCategoryRows, categories) || categoryId
  const selectedCategory = parentCategories.find(c => c.id === effectiveCatId)
    || categories.find(c => c.id === effectiveCatId)
  // Show selected category in pills even if not in recents
  const pillCategories = useMemo(() => {
    if (!selectedCategory) return recentCategories
    if (recentCategories.find(c => c.id === selectedCategory.id)) return recentCategories
    return [selectedCategory, ...recentCategories].slice(0, 5)
  }, [recentCategories, selectedCategory])

  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId)

  // ─── Render: scanner takeover ─────────────────────────
  if (showScanner) {
    return (
      <ReceiptScanner
        mode="receipt"
        onClose={() => setShowScanner(false)}
        onSaved={handleScannerSaved}
      />
    )
  }

  // ─── Render: full category picker (when "All categories" tapped) ──
  if (showAllCats) {
    return (
      <Sheet onClose={onClose}>
        <header style={styles.header}>
          <button
            type="button"
            className="btn-compact"
            style={styles.iconBtn}
            onClick={() => { setShowAllCats(false); setCatSearch('') }}
            aria-label="Back"
          >
            ‹
          </button>
          <span style={styles.headerTitle}>Select category</span>
          <span style={{ width: '36px', height: '36px' }} aria-hidden="true" />
        </header>
        <div style={styles.scrollArea}>
          {categories.length === 0 ? (
            <div style={styles.noCats}>
              No categories yet — set up your budget in Plan first.
            </div>
          ) : (
            <>
              <div style={{ padding: '12px 16px 8px' }}>
                <input
                  type="text"
                  value={catSearch}
                  onChange={(e) => setCatSearch(e.target.value)}
                  placeholder="Search categories…"
                  autoFocus={!showCreateCat}
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    background: c.cream2,
                    border: `1px solid ${c.line}`,
                    borderRadius: '10px',
                    fontFamily: f.body,
                    fontSize: '14.5px',
                    color: c.plum,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />

                {/* Create new category — inline form */}
                {!showCreateCat ? (
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateCat(true)
                      // Pre-fill from search input if user typed something
                      if (catSearch.trim()) setNewCatName(catSearch.trim())
                    }}
                    style={{
                      marginTop: '8px',
                      width: '100%',
                      padding: '10px',
                      background: 'transparent',
                      border: `1px dashed ${c.lineStrong}`,
                      borderRadius: '10px',
                      fontFamily: f.body,
                      fontSize: '13px',
                      fontWeight: 600,
                      color: c.plum,
                      cursor: 'pointer',
                    }}
                  >
                    + Create new category
                  </button>
                ) : (
                  <div style={{
                    marginTop: '8px',
                    padding: '12px',
                    background: c.cream2,
                    border: `1px solid ${c.line}`,
                    borderRadius: '10px',
                  }}>
                    <select
                      value={newCatParent || ''}
                      onChange={(e) => setNewCatParent(e.target.value || null)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        marginBottom: '8px',
                        background: c.white,
                        border: `1px solid ${c.line}`,
                        borderRadius: '8px',
                        fontFamily: f.body,
                        fontSize: '14px',
                        color: c.plum,
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="">No parent (top-level category)</option>
                      {parentCategories.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>

                    <input
                      type="text"
                      value={newCatName}
                      onChange={(e) => setNewCatName(e.target.value)}
                      placeholder="Category name"
                      autoFocus
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        marginBottom: '8px',
                        background: c.white,
                        border: `1px solid ${c.line}`,
                        borderRadius: '8px',
                        fontFamily: f.body,
                        fontSize: '14px',
                        color: c.plum,
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreateCat(false)
                          setNewCatName('')
                          setNewCatParent(null)
                        }}
                        style={{
                          flex: 1,
                          padding: '9px',
                          background: 'transparent',
                          border: `1px solid ${c.line}`,
                          borderRadius: '8px',
                          fontFamily: f.body,
                          fontSize: '13px',
                          fontWeight: 600,
                          color: c.inkMuted,
                          cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!newCatName.trim() || creatingCat) return
                          setCreatingCat(true)
                          const { data: { user } } = await supabase.auth.getUser()
                          const budgetId = targetBudgetId
                          if (!budgetId) {
                            alert('No active budget found. Please create one first in Plan.')
                            setCreatingCat(false)
                            return
                          }
                          const maxSort = categories.reduce((m, c) => Math.max(m, c.sort_order || 0), 0)
                          const { data: created } = await supabase.from('budget_categories').insert({
                            user_id: user.id,
                            budget_id: budgetId,
                            name: newCatName.trim(),
                            limit_amount: 0,
                            sort_order: maxSort + 1,
                            // The deduped parent may be another month's row —
                            // use the target budget's same-named parent if present.
                            parent_id: newCatParent
                              ? (allCategoryRows.find(r => r.budget_id === budgetId && !r.parent_id &&
                                  norm(r.name) === norm(categories.find(p => p.id === newCatParent)?.name)
                                )?.id || newCatParent)
                              : null,
                          }).select().single()
                          if (created) {
                            // Refetch so picker shows it
                            await fetchData()
                            setCategoryId(created.id)
                            emitDataEvent(EVENTS.CATEGORY_SAVED)
                            setShowCreateCat(false)
                            setShowAllCats(false)
                            setNewCatName('')
                            setNewCatParent(null)
                            setCatSearch('')
                          }
                          setCreatingCat(false)
                        }}
                        disabled={!newCatName.trim() || creatingCat}
                        style={{
                          flex: 1,
                          padding: '9px',
                          background: c.plum,
                          color: c.cream,
                          border: 'none',
                          borderRadius: '8px',
                          fontFamily: f.body,
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: (!newCatName.trim() || creatingCat) ? 'not-allowed' : 'pointer',
                          opacity: (!newCatName.trim() || creatingCat) ? 0.5 : 1,
                        }}
                      >
                        {creatingCat ? 'Creating…' : 'Create'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ padding: '8px 0 16px' }}>
                {(() => {
                  const q = catSearch.trim().toLowerCase()
                  if (q) {
                    // When searching, show ALL matching categories (parents + subs) flat
                    const matches = categories.filter(c =>
                      c.name.toLowerCase().includes(q)
                    )
                    if (matches.length === 0) {
                      return (
                        <div style={{
                          padding: '20px', textAlign: 'center',
                          color: c.inkMuted, fontSize: '13.5px',
                        }}>
                          No categories match "{catSearch}"
                        </div>
                      )
                    }
                    return matches.map(cat => {
                      const parent = cat.parent_id ? categories.find(p => p.id === cat.parent_id) : null
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          className="btn-compact"
                          style={{
                            ...styles.catRow,
                            background: effectiveCatId === cat.id ? c.greenSoft : 'transparent',
                            paddingLeft: cat.parent_id ? '40px' : '16px',
                          }}
                          onClick={() => {
                            setCategoryId(cat.id)
                            setShowAllCats(false)
                            setCatSearch('')
                          }}
                        >
                          <span style={styles.catName}>
                            {cat.name}
                            {parent && (
                              <span style={{ color: c.inkMuted, fontWeight: 400, marginLeft: '6px' }}>
                                · {parent.name}
                              </span>
                            )}
                          </span>
                          {effectiveCatId === cat.id && (
                            <span style={{ color: c.green, fontSize: '15px', lineHeight: 1 }} aria-hidden="true">✓</span>
                          )}
                        </button>
                      )
                    })
                  }
                  // Default tree view
                  return parentCategories.map(parent => {
                    const subs = getSubcategories(parent.id)
                return (
                  <div key={parent.id}>
                    <button
                      type="button"
                      className="btn-compact"
                      style={{
                        ...styles.catRow,
                        background: effectiveCatId === parent.id ? c.greenSoft : 'transparent',
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        if (subs.length === 0) {
                          setCategoryId(parent.id)
                          setShowAllCats(false)
                        }
                      }}
                      disabled={subs.length > 0}
                    >
                      <span style={styles.catName}>{parent.name}</span>
                      {subs.length > 0
                        ? <span style={{ color: c.inkQuiet, fontSize: '16px', lineHeight: 1 }} aria-hidden="true">›</span>
                        : effectiveCatId === parent.id
                          ? <span style={{ color: c.green, fontSize: '15px', lineHeight: 1 }} aria-hidden="true">✓</span>
                          : null}
                    </button>
                    {subs.map(sub => (
                      <button
                        key={sub.id}
                        type="button"
                        className="btn-compact"
                        style={{
                          ...styles.catRow,
                          paddingLeft: '52px',
                          background: effectiveCatId === sub.id ? c.greenSoft : 'transparent',
                        }}
                        onClick={() => {
                          setCategoryId(sub.id)
                          setShowAllCats(false)
                        }}
                      >
                        <span style={{ ...styles.catName, fontSize: '14px' }}>{sub.name}</span>
                        {effectiveCatId === sub.id && <span style={{ color: c.green, fontSize: '15px', lineHeight: 1 }} aria-hidden="true">✓</span>}
                      </button>
                    ))}
                    {subs.length > 0 && (
                      <button
                        type="button"
                        className="btn-compact"
                        style={{
                          ...styles.catRow,
                          paddingLeft: '52px',
                          background: effectiveCatId === parent.id ? c.greenSoft : 'transparent',
                        }}
                        onClick={() => {
                          setCategoryId(parent.id)
                          setShowAllCats(false)
                        }}
                      >
                        <span style={{ ...styles.catIcon, color: c.inkQuiet }}>•</span>
                        <span style={{ ...styles.catName, fontSize: '14px', color: c.inkMuted, fontStyle: 'italic' }}>
                          {parent.name} (general)
                        </span>
                        {effectiveCatId === parent.id && <span style={{ color: c.green, fontSize: '15px', lineHeight: 1 }} aria-hidden="true">✓</span>}
                      </button>
                    )}
                  </div>
                )
              })
                })()}
              </div>
            </>
          )}
        </div>
      </Sheet>
    )
  }

  // ─── Render: bill/debt match confirmation (Chunk 5) ───
  if (matchPrompt) {
    return (
      <Sheet onClose={() => { setMatchPrompt(null); onSaved() }}>
        <MatchPrompt
          merchantName={merchantName}
          transactions={matchPrompt.transactions}
          kind={matchPrompt.kind}
          target={matchPrompt.target}
          bills={matchPrompt.bills}
          debts={matchPrompt.debts}
          userId={matchPrompt.userId}
          onDone={() => { setMatchPrompt(null); onSaved() }}
          onCancel={() => { setMatchPrompt(null); onSaved() }}
        />
      </Sheet>
    )
  }

  // ─── Render: saved confirmation ───────────────────────
  if (saved) {
    return (
      <Sheet onClose={onClose}>
        <div style={styles.savedState}>
          <div style={styles.savedIcon}>
            <span style={{ fontSize: '30px', lineHeight: 1 }} aria-hidden="true">✓</span>
          </div>
          <div style={styles.savedText}>Saved</div>
          <div style={styles.savedSub}>
            ${parseFloat(amount).toFixed(2)}
            {selectedCategory ? ` · ${selectedCategory.name}` : ''}
          </div>
        </div>
      </Sheet>
    )
  }

  // ─── Render: main view (Phone 3) ──────────────────────
  return (
    <Sheet onClose={onClose}>

      {/* Header */}
      <header style={styles.header}>
        <button
          type="button"
          className="btn-compact"
          style={styles.iconBtn}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <span style={styles.headerTitle}>
          {prefill ? 'Repeat transaction' : 'Add transaction'}
        </span>
        <button
          type="button"
          className="btn-compact"
          style={{ ...styles.iconBtn, width: 'auto', padding: '0 14px', background: c.greenSoft, color: c.green, fontSize: '13px', fontWeight: 600 }}
          onClick={() => setShowScanner(true)}
          aria-label="Scan receipt"
          title="Scan receipt"
        >
          Scan
        </button>
      </header>

      {/* Scrollable region: everything ABOVE the numpad. The numpad and the
          save bar are pinned below so the keypad (incl. the decimal key) is
          always fully visible regardless of viewport height. */}
      <div style={styles.scrollArea}>

        {/* Hero amount display */}
        <div style={styles.amountDisplay}>
          <div style={styles.amountLabel}>Amount</div>
          <div style={styles.amountWrap}>
            <span style={styles.amountCurrency}>$</span>
            <span style={{
              ...styles.amountWhole,
              color: amountDisplay.isPlaceholder ? c.inkQuiet : c.plum,
            }}>
              {amountDisplay.whole}
            </span>
            {amountDisplay.hasDot ? (
              <span style={{
                ...styles.amountCents,
                color: amountDisplay.isPlaceholder ? c.inkQuiet : c.plumSoft,
              }}>
                .{amountDisplay.cents}
              </span>
            ) : (
              <span style={{
                ...styles.amountCents,
                color: c.inkQuiet,
              }}>
                .{amountDisplay.cents}
              </span>
            )}
          </div>
        </div>

        {/* Merchant row */}
        <div style={styles.merchantRow} ref={merchantRef}>
          <input
            type="text"
            value={merchantName}
            onChange={(e) => setMerchantName(e.target.value)}
            placeholder="Where? e.g. Kroger, Starbucks"
            autoComplete="off"
            style={styles.merchantInput}
            aria-label="Merchant"
          />
          {suggestedCategory && (
            <span style={styles.merchantSuggest} aria-label={`Auto-suggested category: ${suggestedCategory.name}`}>
              ↑ {suggestedCategory.name}
            </span>
          )}
        </div>

        {/* Date + Account row */}
        <div style={styles.metaRow}>
          <label style={styles.metaField}>
            <input
              type="date"
              value={txDate}
              onChange={(e) => setTxDate(e.target.value)}
              style={styles.metaInput}
              aria-label="Transaction date"
            />
          </label>
          <label style={styles.metaField}>
            <select
              value={paymentMethodId || ''}
              onChange={(e) => setPaymentMethodId(e.target.value || null)}
              style={styles.metaInput}
              aria-label="Payment method"
            >
              <option value="">Cash / no card</option>
              {paymentMethods.map(pm => (
                <option key={pm.id} value={pm.id}>
                  {pm.name}{pm.last_four ? ` ··${pm.last_four}` : ''}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Personal / Business / Charity toggle */}
        <div style={styles.typeToggle}>
          {[
            { value: 'personal', label: 'Personal' },
            { value: 'business', label: 'Business' },
            { value: 'charitable', label: 'Charity' },
          ].map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setTxType(opt.value)}
              style={{
                ...styles.typeBtn,
                ...(txType === opt.value ? styles.typeBtnActive : {}),
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Quick category pills.
            Strict mode: only show pills if user has either
              (a) selected a category (so they see what's chosen), OR
              (b) typed a merchant we recognize (auto-suggested match)
            Otherwise: just the "Pick category" button. No recent shortcuts.
        */}
        {categories.length > 0 && (
          <div style={styles.quickCatStrip}>
            {(() => {
              const hasMatch = !!suggestedCategory
              const hasManualPick = !!categoryId
              const showPills = hasMatch || hasManualPick

              if (!showPills) {
                return (
                  <button
                    type="button"
                    className="btn-compact"
                    style={{ ...styles.quickCatPill, ...styles.quickCatPillMore }}
                    onClick={() => setShowAllCats(true)}
                  >
                    Pick category
                  </button>
                )
              }

              // Show only the selected/matched category as a pill.
              // The "More…" button always lets users open the full picker.
              const selected = categories.find(c => c.id === effectiveCatId) || suggestedCategory
              return (
                <>
                  {selected && (
                    <button
                      type="button"
                      className="btn-compact"
                      style={{
                        ...styles.quickCatPill,
                        ...styles.quickCatPillActive,
                      }}
                      onClick={() => setCategoryId(selected.id)}
                    >
                      {selected.name}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-compact"
                    style={{ ...styles.quickCatPill, ...styles.quickCatPillMore }}
                    onClick={() => setShowAllCats(true)}
                  >
                    Change…
                  </button>
                </>
              )
            })()}
          </div>
        )}

        {/* No-categories empty state */}
        {categories.length === 0 && (
          <div style={styles.noCatsInline}>
            No categories yet — set up your budget in Plan first.
          </div>
        )}

      </div>

      {/* Numpad — pinned (outside scrollArea) so all four rows, including the
          decimal key, are always visible. flexShrink:0 keeps it from being
          compressed when the viewport is short. */}
      <div style={styles.numpad}>
        {['1','2','3','4','5','6','7','8','9'].map(n => (
          <button
            key={n}
            type="button"
            className="btn-compact"
            style={styles.numKey}
            onClick={() => handleNumpadKey(n)}
          >
            {n}
          </button>
        ))}
        <button
          type="button"
          className="btn-compact"
          style={{ ...styles.numKey, ...styles.numKeyFn }}
          onClick={() => handleNumpadKey('.')}
          aria-label="Decimal"
        >
          .
        </button>
        <button
          type="button"
          className="btn-compact"
          style={styles.numKey}
          onClick={() => handleNumpadKey('0')}
        >
          0
        </button>
        <button
          type="button"
          className="btn-compact"
          style={{ ...styles.numKey, ...styles.numKeyFn }}
          onClick={() => handleNumpadKey('del')}
          aria-label="Delete"
        >
          <span aria-hidden="true" style={{ fontSize: '22px', lineHeight: 1 }}>⌫</span>
        </button>
      </div>

      {/* Persistent save bar */}
      <div style={styles.saveBar}>
        {(!parseFloat(amount) || !categoryId) && (
          <div style={{
            fontSize: '12px', color: c.inkMuted,
            textAlign: 'center', marginBottom: '8px',
          }}>
            {!parseFloat(amount) && !categoryId
              ? 'Enter an amount and pick a category'
              : !parseFloat(amount)
                ? 'Enter an amount'
                : 'Pick a category'}
          </div>
        )}
        <button
          type="button"
          style={{
            ...styles.saveBtn,
            opacity: (!parseFloat(amount) || !categoryId || saving) ? 0.4 : 1,
            cursor: (!parseFloat(amount) || !categoryId || saving) ? 'not-allowed' : 'pointer',
          }}
          onClick={handleSave}
          disabled={!parseFloat(amount) || !categoryId || saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

    </Sheet>
  )
}

// ─────────────────────────────────────────────────────────────
// Sheet shell
// ─────────────────────────────────────────────────────────────

function Sheet({ children, onClose }) {
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768
  return (
    <div
      style={{
        ...styles.overlay,
        alignItems: isDesktop ? 'center' : 'flex-end',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      role="dialog"
      aria-modal="true"
      aria-label="Add transaction"
    >
      <div
        style={{
          ...styles.sheet,
          borderRadius: isDesktop ? '20px' : '20px 20px 0 0',
          maxHeight: isDesktop ? '85vh' : '94vh',
        }}
      >
        {!isDesktop && <div style={styles.sheetHandle} aria-hidden="true" />}
        {children}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────

const c = theme.colors
const f = theme.fonts

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    overscrollBehaviorX: 'contain',
    background: 'rgba(30, 16, 64, 0.6)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    zIndex: 900,
    fontFamily: f.body,
  },
  sheet: {
    background: c.cream,
    borderRadius: '20px 20px 0 0',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '94vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 -8px 40px rgba(30, 16, 64, 0.2)',
    overscrollBehaviorX: 'contain',
  },
  sheetHandle: {
    width: '40px',
    height: '4px',
    borderRadius: '2px',
    background: c.cream3,
    margin: '10px auto 0',
    flexShrink: 0,
  },
  header: {
    padding: '12px 22px 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  headerTitle: {
    fontFamily: f.display,
    fontWeight: 600,
    fontSize: '20px',
    letterSpacing: '-0.02em',
    color: c.plum,
  },
  iconBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'rgba(30, 16, 64, 0.05)',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    color: c.plum,
    border: 'none',
    fontSize: '22px',
    lineHeight: 1,
  },

  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    paddingBottom: '4px',
    minHeight: 0,
    // The actual scroll container — contain horizontal overscroll here so a
    // drag-select inside the modal can't trigger the browser back-gesture.
    overscrollBehaviorX: 'contain',
  },

  // Hero amount display
  amountDisplay: {
    textAlign: 'center',
    padding: '12px 22px 14px',
  },
  amountLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: c.inkMuted,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '6px',
  },
  amountWrap: {
    fontFamily: f.display,
    fontWeight: 600,
    fontSize: '54px',
    lineHeight: 1,
    letterSpacing: '-0.035em',
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: '3px',
    minHeight: '54px',
  },
  amountCurrency: {
    fontSize: '24px',
    fontWeight: 500,
    color: c.plumSoft,
    transform: 'translateY(-14px)',
  },
  amountWhole: {
    transition: 'color 120ms ease',
  },
  amountCents: {
    fontSize: '30px',
    fontWeight: 500,
    transform: 'translateY(-12px)',
    transition: 'color 120ms ease',
  },

  // Merchant row
  merchantRow: {
    margin: '0 22px 12px',
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '14px',
    padding: '12px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'text',
  },
  metaRow: {
    display: 'flex',
    gap: '8px',
    margin: '0 22px 12px',
    flexWrap: 'wrap',
  },
  metaField: {
    flex: 1,
    minWidth: '140px',
    display: 'flex',
    alignItems: 'center',
    background: c.cream2,
    border: `1px solid ${c.line}`,
    borderRadius: '10px',
    padding: '8px 12px',
    cursor: 'pointer',
  },
  metaInput: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    fontFamily: f.body,
    fontSize: '13.5px',
    color: c.plum,
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
  },
  typeToggle: {
    display: 'flex',
    gap: '6px',
    margin: '0 22px 14px',
    background: c.cream2,
    borderRadius: '10px',
    padding: '4px',
  },
  typeBtn: {
    flex: 1,
    padding: '8px 12px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    fontFamily: f.body,
    fontSize: '13px',
    fontWeight: 500,
    color: c.inkMuted,
    cursor: 'pointer',
    transition: 'all 120ms ease',
  },
  typeBtnActive: {
    background: c.white,
    color: c.plum,
    fontWeight: 600,
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
  merchantIconWrap: {
    width: '32px',
    height: '32px',
    borderRadius: '10px',
    background: c.cream3,
    color: c.plumSoft,
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
  },
  merchantInput: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    fontFamily: f.body,
    fontSize: '15px',
    fontWeight: 600,
    color: c.plum,
    outline: 'none',
    padding: 0,
    minWidth: 0,
  },
  merchantSuggest: {
    fontSize: '11.5px',
    color: c.green,
    fontWeight: 600,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },

  // Quick category pills
  quickCatStrip: {
    margin: '4px 0 8px',
    padding: '0 22px',
    overflowX: 'auto',
    display: 'flex',
    gap: '8px',
    scrollbarWidth: 'none',
    WebkitOverflowScrolling: 'touch',
  },
  quickCatPill: {
    flexShrink: 0,
    padding: '8px 14px',
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '999px',
    fontSize: '13px',
    fontWeight: 600,
    color: c.plum,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: f.body,
    transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
  },
  quickCatPillActive: {
    background: c.plum,
    color: c.cream,
    borderColor: c.plum,
  },
  quickCatPillMore: {
    background: 'transparent',
    color: c.inkMuted,
    borderStyle: 'dashed',
  },

  // Empty state
  noCatsInline: {
    margin: '8px 22px 8px',
    padding: '12px 14px',
    background: c.orangeSoft,
    color: c.orange,
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 500,
    textAlign: 'center',
  },

  // Icon picker grid (category create)
  iconGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '6px',
  },
  iconCell: {
    aspectRatio: '1 / 1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '10px',
    cursor: 'pointer',
    padding: 0,
    transition: 'background 120ms ease, border-color 120ms ease',
  },

  // Numpad
  numpad: {
    margin: '6px 22px 10px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '6px',
    flexShrink: 0,
  },
  numKey: {
    aspectRatio: '3 / 1',
    background: 'rgba(30, 16, 64, 0.04)',
    border: 'none',
    borderRadius: '12px',
    fontFamily: f.display,
    fontWeight: 600,
    fontSize: '22px',
    color: c.plum,
    cursor: 'pointer',
    transition: 'background 100ms ease',
    letterSpacing: '-0.01em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  numKeyFn: {
    fontFamily: f.body,
    fontSize: '18px',
    fontWeight: 600,
    color: c.inkMuted,
  },

  // Save bar
  saveBar: {
    padding: '14px 22px calc(env(safe-area-inset-bottom, 12px) + 12px)',
    background: 'rgba(250, 247, 242, 0.92)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderTop: `1px solid ${c.line}`,
    flexShrink: 0,
  },
  saveBtn: {
    width: '100%',
    height: '52px',
    background: c.plum,
    color: c.cream,
    border: 'none',
    borderRadius: '14px',
    fontFamily: f.body,
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'transform 100ms ease, opacity 120ms ease',
  },

  // Full category picker (when "More…" tapped)
  catRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 22px',
    cursor: 'pointer',
    borderBottom: `1px solid ${c.line}`,
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    transition: 'background 120ms ease',
    fontFamily: f.body,
    color: c.plum,
  },
  catIcon: {
    fontSize: '18px',
    width: '24px',
    textAlign: 'center',
    flexShrink: 0,
    color: c.plumSoft,
  },
  catName: {
    flex: 1,
    fontSize: '15px',
    color: c.plum,
  },
  noCats: {
    margin: '20px 22px',
    padding: '14px',
    background: c.orangeSoft,
    color: c.orange,
    borderRadius: '12px',
    fontSize: '13.5px',
    fontWeight: 500,
    textAlign: 'center',
  },

  // Saved confirmation
  savedState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 22px 80px',
  },
  savedIcon: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    background: c.green,
    color: '#FFFFFF',
    display: 'grid',
    placeItems: 'center',
    marginBottom: '18px',
    boxShadow: '0 6px 18px -4px rgba(22, 163, 74, 0.4)',
  },
  savedText: {
    fontFamily: f.display,
    fontSize: '28px',
    fontWeight: 600,
    color: c.plum,
    letterSpacing: '-0.02em',
  },
  savedSub: {
    marginTop: '6px',
    fontSize: '14px',
    color: c.inkMuted,
  },
}
